import { Card, Col, Progress, Row, Grid, Skeleton } from "antd";
import flexibleFormat from "../../functions/flexibleFormat";

const { useBreakpoint } = Grid;

const COLOR_CHART = [
  "rgba(185, 214, 199, 1)",
  "rgba(122, 123, 173, 1)",
  "rgba(37, 27, 27, 0.4)",
  "rgba(185, 214, 199, 0.5)",
  "rgba(255, 255, 255, 0)",
  "rgba(188, 187, 233, 1)",
  "rgba(37, 27, 27, 0.4)",
  "rgba(185, 214, 199, 0.9)",
  "rgba(122, 123, 173, 0.9)",
];
// helper: group items by key and sum numeric values
function groupBySum(items, keyFn, valueFn) {
  const map = new Map();
  for (const item of items) {
    const key = keyFn(item);
    const add = valueFn(item) || 0;
    map.set(key, (map.get(key) || 0) + add);
  }
  return Array.from(map, ([key, total]) => ({ key, total }));
}

// helper: pad an array to at least n entries
function padTo(arr, n, filler) {
  const copy = [...arr];
  while (copy.length < n) copy.push({ ...filler });
  return copy;
}

export const RankingCard = (props) => {
  const { title, year, loading } = props;
  const { emissionData, abatementData } = props;
  const { totalEmission, totalAbatement } = props;
  const screens = useBreakpoint();

  let emissionDataCopy = [...emissionData];
  let abatementDataCopy = [...abatementData];

  const PROTO_ROW_SPANS = [10, 9, 5];
  abatementDataCopy.sort(
    (a, b) => b.Quantity * b.EmissionFactor - a.Quantity * a.EmissionFactor
  );
  // --- EMISSIONS: group by ActiveName for the chosen year ---
  const emissionsGrouped = groupBySum(
    emissionDataCopy.filter((e) => e.InventoryYear === year.toString()),
    (e) => e.ActiveName, // group key
    (e) => e.TotalCO2e // numeric value to sum
  );

  // sort descending by total tCO2e and keep top 3
  const rankedEmission = padTo(
    emissionsGrouped.sort((a, b) => b.total - a.total).slice(0, 3),
    3,
    { key: "No Data", total: 0 }
  );

  const emissionItems = rankedEmission.map((g, index) => {
    const percent = totalEmission
      ? Math.floor((g.total / totalEmission) * 100)
      : 0;

    return (
      <ProtoRow
        key={`em-${g.key}-${index}`}
        rank={index + 1}
        text={g.key}
        percent={percent}
        value={g.total}
        color={COLOR_CHART[1]}
      />
    );
  });

  function ProtoRow({ rank, text, percent, value, color }) {
    let renderValue = value;

    if (typeof renderValue == "number") {
      renderValue = flexibleFormat(renderValue);
    }
    return (
      <Row style={{ alignItems: "center" }}>
        <Col
          span={PROTO_ROW_SPANS[0]}
          style={{ padding: "8px", textAlign: "left", fontSize: 12 }}
        >
          {text}
        </Col>
        <Col
          span={PROTO_ROW_SPANS[1]}
          className="ranking-card-progress"
          style={{ padding: "8px" }}
        >
          <Progress
            percent={percent}
            size={"small"}
            strokeColor={color}
            status="normal"
          />
        </Col>
        <Col
          span={PROTO_ROW_SPANS[2]}
          style={{ textAlign: "right", padding: "8px" }}
        >
          {renderValue}
        </Col>
      </Row>
    );
  }

  const rankedAbatement = abatementDataCopy
    .filter((element) => element.Year === year.toString())
    .slice(0, 3);
  while (rankedAbatement.length < 3) {
    rankedAbatement.push({
      Name: "No Data",
      Quantity: 0,
      EmissionFactor: 0,
    });
  }
  const abatementItems = rankedAbatement.map((element, index) => {
    const abatement = element.Quantity * element.EmissionFactor;
    const percent = Math.floor((abatement / totalAbatement) * 100);
    return (
      <ProtoRow
        text={element.Name}
        value={abatement}
        percent={percent}
        color={COLOR_CHART[0]}
      />
    );
  });

  return (
    <Card
      size="small"
      style={{
        width: "100%",
        height: screens.xl ? "100%" : "auto",
      }}
    >
      <div style={{ width: "100%", height: screens.xl ? "50%" : "auto" }}>
        <h5
          style={{
            margin: "0px",
            fontSize: "17.6px",
          }}
        >
          Top 3 Emission Sources
        </h5>
        {loading ? (
          <div
            style={{
              width: "100%",
            }}
          >
            <Skeleton active />
          </div>
        ) : (
          <div
            style={{
              width: "100%",
            }}
          >
            <div style={{ width: "100%" }}>
              <Row style={{ fontWeight: "bold", fontSize: 12 }}>
                <Col span={PROTO_ROW_SPANS[0]} style={{ textAlign: "left" }}>
                  Name
                </Col>
                <Col span={PROTO_ROW_SPANS[1]} style={{ textAlign: "center" }}>
                  Percentage
                </Col>
                <Col span={PROTO_ROW_SPANS[2]} style={{ textAlign: "right" }}>
                  tCO<span>2</span>e
                </Col>
              </Row>
              {emissionItems}
            </div>
          </div>
        )}
      </div>
      <h5
        style={{
          margin: "0px",
          fontSize: "17.6px",
        }}
      >
        Top 3 Abatement Initiatives
      </h5>
      {loading ? (
        <div
          style={{
            width: "100%",
          }}
        >
          <Skeleton active />
        </div>
      ) : (
        <div
          style={{
            width: "100%",
          }}
        >
          <div style={{ width: "100%" }}>
            <Row style={{ fontWeight: "bold", fontSize: 12 }}>
              <Col span={PROTO_ROW_SPANS[0]} style={{ textAlign: "left" }}>
                Name
              </Col>
              <Col span={PROTO_ROW_SPANS[1]} style={{ textAlign: "center" }}>
                Percentage
              </Col>
              <Col span={PROTO_ROW_SPANS[2]} style={{ textAlign: "right" }}>
                tCO<span>2</span>e
              </Col>
            </Row>
            {abatementItems}
          </div>
        </div>
      )}
    </Card>
  );
};
